// Copyright (c) 2026. Hardfin, Inc. All rights reserved.
// Hardfin Confidential - Restricted

/**
 * Customer listing page route module.
 * Provides the main interface for viewing and managing customers.
 *
 * Features:
 * - Customer list with sorting and filtering
 * - Search functionality
 * - Archive filter toggle
 * - Customer creation dialog
 * - Customer detail drawer
 * - Local storage preferences
 *
 * URL Parameters:
 * - query: Search text for filtering customers
 * - customer: Selected customer ID for drawer
 * - archived: Whether to show archived customers
 * - direction: Sort direction (ascending/descending)
 * - sort: Sort property (name, etc.)
 * - new: Name for new customer creation
 */
import { PageContent } from '@/components/page-content';
import { TableControlContainer } from '@/components/table-control-container';
import {
  AddRounded,
  SettingsRounded,
} from '@/constants/hardfin-standard-icons';
import { SEARCH_TERM_MAXIMUM_CHARACTERS } from '@/constants/search-term';
import { SortDirection, invert, sortDirectionSchema } from '@/constants/sort';
import {
  CUSTOMER_PAGE_SIZE,
  CustomerFilter,
} from '@/customer/api/get-customers';
import { CreateCustomerDialog } from '@/customer/components/create-customer-dialog';
import { CustomerTable } from '@/customer/components/customer-table';
import {
  CustomerSortProperty,
  customerSortPropertySchema,
} from '@/customer/hooks/use-customers';
import { useDisclosure } from '@/hooks/use-disclosure';
import { useLocalStorage } from '@/hooks/use-local-storage';
import { searchTermSchema } from '@/utilities/search-term';
import {
  Badge,
  Button,
  Checkbox,
  FormControlLabel,
  IconButton,
  Popover,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { createFileRoute } from '@tanstack/react-router';
import { debounce, omit } from 'lodash-es';
import { ChangeEventHandler, Suspense, useMemo, useRef } from 'react';
import { z } from 'zod';

/**
 * Local storage key for persisting sort preferences.
 * Maintains user's preferred sorting across sessions.
 */
const LOCAL_STORAGE_SORT_KEY = 'customer-sort';

/**
 * Default sort property when no preference is set.
 * Customers are sorted by name initially.
 */
const DEFAULT_SORT_PROPERTY: CustomerSortProperty = 'name';

/**
 * Default sort direction when no preference is set.
 * Customers are sorted in ascending order initially.
 */
const DEFAULT_SORT_DIRECTION: SortDirection = 'asc';

/**
 * Tooltip text explaining the archived filter.
 * Helps users understand what archived customers are.
 */
const SHOW_ARCHIVED_TOOLTIP_TITLE =
  'Show all customer records, including archived customers. Archived customers do not appear by default in the customer list.';

/**
 * Schema for validating URL search parameters.
 * Ensures type safety for route parameters.
 */
const searchSchema = z.object({
  query: searchTermSchema,
  customer: z.string().optional(),
  archived: z.boolean().optional(),
  direction: sortDirectionSchema.optional(),
  sort: customerSortPropertySchema.optional(),
  page: z.number().optional(),
  new: z.string().optional(),
});

/**
 * Schema for validating sort preferences.
 * Used for both URL parameters and local storage.
 */
const sortSchema = z.object({
  property: customerSortPropertySchema,
  direction: sortDirectionSchema,
});

/**
 * Customer listing route configuration.
 * Redirects to the companies page with customers view.
 * This route is maintained for backwards compatibility.
 */
export const Route = createFileRoute('/_app/customers/')({
  component: CustomersRoute,
  validateSearch: searchSchema,
  beforeLoad: ({ navigate }) => {
    navigate({ to: '/companies/customers', replace: true });
  },
});

/**
 * Main customer listing route component.
 * Manages the customer list interface and related dialogs.
 *
 * Features:
 * - Customer table with sorting and filtering
 * - Customer search with debounced updates
 * - Archive filter toggle
 * - Customer creation dialog
 * - Customer detail drawer
 * - Sort preference persistence
 *
 * URL Integration:
 * - Maintains state in URL parameters
 * - Supports deep linking to customers
 * - Preserves filters across refreshes
 */
function CustomersRoute() {
  /**
   * Current URL search parameters and navigation function.
   * Used to maintain state in the URL and handle routing.
   */
  const search = Route.useSearch();
  const navigate = Route.useNavigate();

  /**
   * Dialog and drawer state management.
   * - dialog: Controls the create customer dialog visibility
   * - drawer: Controls the customer detail drawer visibility
   *
   * Both are initialized based on URL parameters to support deep linking:
   * - dialog opens if 'new' parameter exists
   * - drawer opens if 'customer' parameter exists
   */
  const dialog = useDisclosure(Boolean(search.new));
  const menu = useDisclosure();
  const menuButtonRef = useRef<HTMLButtonElement>(null);

  /**
   * Sort preference persistence in local storage.
   * Maintains user's preferred sorting between sessions.
   * Falls back to default sorting if no preference exists.
   */
  const [sortPreference, setSortPreference] = useLocalStorage(
    LOCAL_STORAGE_SORT_KEY,
    sortSchema
  );

  /**
   * Debounced search query handler.
   * Updates URL parameters after user stops typing.
   * 300ms delay prevents excessive URL updates.
   */
  const updateQuery = useMemo(() => {
    return debounce(
      (query: string) =>
        navigate({ search: { ...omit(search, 'page'), query } }),
      300
    );
  }, [navigate, search]);

  /**
   * Current sort configuration.
   * Prioritizes URL parameters over local storage preferences.
   * Falls back to defaults if neither exists.
   *
   * Order of precedence:
   * 1. URL search parameters
   * 2. Local storage preferences
   * 3. Default values
   */
  const sort = {
    property: search.sort ?? sortPreference?.property ?? DEFAULT_SORT_PROPERTY,
    direction:
      search.direction ?? sortPreference?.direction ?? DEFAULT_SORT_DIRECTION,
  };

  /**
   * Current filter configuration for the customer table.
   * Combines archive state and search query.
   *
   * Archive filter:
   * - undefined: Show only active customers
   * - 'all': Show both active and archived
   */
  const filter: CustomerFilter = {
    isArchived: search.archived ? 'all' : undefined,
    query: search.query,
    page: search.page ?? 1,
    pageSize: CUSTOMER_PAGE_SIZE,
  };

  /**
   * Derived state for UI controls.
   * - isArchivedChecked: Current state of archive toggle
   * - defaultCustomerName: Pre-filled name for new customer
   */
  const isArchivedChecked = search.archived ?? false;
  const defaultCustomerName = search.new
    ? decodeURIComponent(search.new)
    : undefined;
  const isBadgeInvisible = search.archived === undefined;

  /**
   * Updates sort configuration.
   * Saves preference to local storage and updates URL.
   *
   * @param property - Column to sort by
   * @param direction - Sort direction (ascending/descending)
   */
  const setSort = (
    property: CustomerSortProperty,
    direction: SortDirection
  ) => {
    setSortPreference({ property, direction });
    navigate({
      search: { ...omit(search, 'page'), direction, sort: property },
    });
  };

  // Customer row clicks now navigate directly to customer detail pages

  /**
   * Handles search input changes.
   * Debounces updates to prevent excessive URL changes.
   */
  const handleChangeQuery: ChangeEventHandler<HTMLInputElement> = (event) => {
    updateQuery(event.target.value);
  };

  /**
   * Handles archive filter toggle.
   * Updates URL to show/hide archived customers.
   *
   * @param isArchived - Whether to show archived customers
   */
  const handleChangeArchivedFilter = (isArchived: boolean) => {
    if (isArchived) {
      return navigate({ search: { ...omit(search, 'page'), archived: true } });
    }
    navigate({ search: { ...omit(search, 'page'), archived: undefined } });
  };

  /**
   * Handles create dialog close.
   * Closes dialog and removes 'new' parameter from URL.
   */
  const handleCloseDialog = () => {
    dialog.close();
    navigate({ search: omit(search, 'new') });
  };

  /**
   * Handles column header click for sorting.
   * Toggles direction if same column, otherwise sorts ascending.
   *
   * @param property - Column to sort by
   */
  const handleSort = (property: CustomerSortProperty) => {
    if (property === sort.property) {
      return setSort(property, invert(sort.direction));
    }
    setSort(property, 'asc');
  };

  return (
    <Stack>
      {/* Create customer dialog */}
      <CreateCustomerDialog
        defaultName={defaultCustomerName}
        isOpen={dialog.isOpen}
        onClose={handleCloseDialog}
      />

      {/* Main content */}
      <PageContent>
        {/* Action bar */}
        <Stack direction="row" justifyContent="end">
          <Button
            variant="contained"
            startIcon={<AddRounded />}
            onClick={dialog.open}
          >
            Add customer
          </Button>
        </Stack>

        {/* Table controls */}
        <TableControlContainer>
          <Stack direction="row" marginRight="auto" spacing={1}>
            {/* Any other controls can go here */}
          </Stack>
          <TextField
            size="small"
            label="Search customers"
            defaultValue={search.query}
            inputProps={{ maxLength: SEARCH_TERM_MAXIMUM_CHARACTERS }}
            onChange={handleChangeQuery}
            sx={{ width: { xs: '100%', sm: '20rem' } }}
          />
          <IconButton ref={menuButtonRef} onClick={menu.open}>
            <Badge variant="dot" color="error" invisible={isBadgeInvisible}>
              <SettingsRounded fontSize="small" />
            </Badge>
          </IconButton>
          <Popover
            anchorEl={menuButtonRef.current}
            open={menu.isOpen}
            onClose={menu.close}
            anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
            transformOrigin={{ vertical: 'top', horizontal: 'right' }}
          >
            <Stack paddingY={0.5}>
              <Tooltip
                arrow
                placement="left"
                title={SHOW_ARCHIVED_TOOLTIP_TITLE}
              >
                <FormControlLabel
                  sx={{ paddingLeft: 2, paddingY: 0.5 }}
                  label={
                    <Typography variant="body2">
                      Include archived customers
                    </Typography>
                  }
                  control={
                    <Checkbox
                      size="small"
                      checked={isArchivedChecked}
                      onChange={(_, isChecked) =>
                        handleChangeArchivedFilter(isChecked)
                      }
                    />
                  }
                />
              </Tooltip>
            </Stack>
          </Popover>
        </TableControlContainer>

        {/* Customer table */}
        <Suspense>
          <CustomerTable
            sort={sort}
            filter={filter}
            onSort={handleSort}
            onPageChange={(page) => navigate({ search: { ...search, page } })}
          />
        </Suspense>
      </PageContent>
    </Stack>
  );
}
